import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import MKBox from 'components/MKBox';
import MKButton from 'components/MKButton';
import MKTypography from 'components/MKTypography';

import { commonInputProps, selectInputProps, secondaryButtonSx } from '../utils/constants';
import colombiaData from '../utils/colombia.json';
import { searchEscoSkills } from '../utils/escoApi';

const filter = createFilterOptions();

function GeneralInfoForm({ formData, setFormData, errors, handleChange, setActiveTab }) {
  const municipiosDisponibles = useMemo(() => {
    const dep = colombiaData.find((d) => d.departamento === formData.departamento);
    return dep ? dep.ciudades : [];
  }, [formData.departamento]);

  const [openSkills, setOpenSkills] = useState(false);
  const [skillOptions, setSkillOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    let active = true;
    if (inputValue.length < 3) {
      setSkillOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchEscoSkills(inputValue);
        if (active) setSkillOptions(results);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue]);

  const handleSkillChange = (e, newValue) => {
    const unique = newValue.reduce((acc, curr) => {
      const raw = typeof curr === 'string' ? curr : curr.inputValue || curr.label;
      const normalized = raw.trim().toLowerCase();
      if (!acc.some((i) => i.normalized === normalized)) {
        acc.push({
          id: curr.id || `custom-${Date.now()}`,
          label: raw,
          source: curr.source || 'CUSTOM',
          normalized,
        });
      }
      return acc;
    }, []);
    setFormData({ ...formData, habilidades: unique });
  };

  return (
    <MKBox component="form" width="100%" autoComplete="off">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nombres"
            value={formData.nombres}
            onChange={handleChange('nombres')}
            error={!!errors.nombres}
            helperText={errors.nombres}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Apellidos"
            value={formData.apellidos}
            onChange={handleChange('apellidos')}
            error={!!errors.apellidos}
            helperText={errors.apellidos}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            type="date"
            label="Fecha de nacimiento"
            value={formData.fecha}
            onChange={handleChange('fecha')}
            error={!!errors.fecha}
            helperText={errors.fecha}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Género"
            value={formData.genero}
            onChange={handleChange('genero')}
            {...selectInputProps}
          >
            <MenuItem value="male">Masculino</MenuItem>
            <MenuItem value="female">Femenino</MenuItem>
            <MenuItem value="other">Otro</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Correo electrónico"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Número de Teléfono"
            value={formData.telefono}
            onChange={handleChange('telefono')}
            error={!!errors.telefono}
            helperText={errors.telefono}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={colombiaData.map((d) => d.departamento)}
            value={formData.departamento || null}
            onChange={(_, val) =>
              setFormData({ ...formData, departamento: val || '', municipio: '' })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Departamento"
                error={!!errors.departamento}
                helperText={errors.departamento}
                {...commonInputProps}
                sx={{
                  ...commonInputProps.sx,
                  '& .MuiAutocomplete-inputRoot': { padding: '0 14px !important' },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={municipiosDisponibles}
            value={formData.municipio || null}
            onChange={(_, val) => setFormData({ ...formData, municipio: val || '' })}
            disabled={!formData.departamento}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Municipio"
                error={!!errors.municipio}
                helperText={errors.municipio}
                {...commonInputProps}
                sx={{
                  ...commonInputProps.sx,
                  '& .MuiAutocomplete-inputRoot': { padding: '0 14px !important' },
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Profesión"
            placeholder="Ej: Ingeniero de Sistemas"
            value={formData.profesion}
            onChange={handleChange('profesion')}
            error={!!errors.profesion}
            helperText={errors.profesion}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Oficio u Ocupación actual"
            placeholder="Ej: Desarrollador Frontend"
            value={formData.oficio}
            onChange={handleChange('oficio')}
            error={!!errors.oficio}
            helperText={errors.oficio}
            {...commonInputProps}
          />
        </Grid>
        <Grid item xs={12}>
          <MKBox mb={1}>
            <MKTypography variant="h6" fontWeight="bold" color="text">
              Habilidades Técnicas
            </MKTypography>
          </MKBox>
          <Autocomplete
            multiple
            freeSolo
            autoSelect
            autoHighlight
            open={openSkills && (inputValue.length >= 3 || loading)}
            onOpen={() => setOpenSkills(true)}
            onClose={() => setOpenSkills(false)}
            inputValue={inputValue}
            onInputChange={(_, val) => setInputValue(val)}
            onChange={handleSkillChange}
            options={skillOptions}
            loading={loading}
            value={formData.habilidades}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              const { inputValue: search } = params;
              const exists = options.some((opt) => opt.label === search);
              if (search !== '' && !exists) {
                filtered.push({ inputValue: search, label: `Agregar "${search}"` });
              }
              return filtered;
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              if (option.inputValue) return option.inputValue;
              return option.label;
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={typeof option === 'string' ? option : option.label}
                  {...getTagProps({ index })}
                  key={index}
                  sx={{
                    backgroundColor: '#1A73E8 !important',
                    color: '#ffffff !important',
                    fontWeight: '500 !important',
                    borderRadius: '4px !important',
                    margin: '2px !important',
                    height: '32px !important',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255,255,255,0.7) !important',
                      '&:hover': { color: '#ffffff !important' },
                    },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={formData.habilidades.length === 0 ? 'Busca o agrega habilidades' : ''}
                placeholder="Ej: React, Gestión..."
                error={!!errors.habilidades}
                helperText={errors.habilidades}
                {...commonInputProps}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="No se encontraron resultados"
          />
          <MKTypography variant="caption" color="text" opacity={0.6} ml={1} mt={1} display="block">
            Usa <b>Enter</b> para agregar habilidades nuevas que no estén en la lista.
          </MKTypography>
        </Grid>
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <MKButton variant="contained" size="large" href="/AboutUs" sx={secondaryButtonSx}>
            Cancelar
          </MKButton>
          <MKButton variant="contained" color="info" size="large" onClick={() => setActiveTab(1)}>
            Siguiente
          </MKButton>
        </Grid>
      </Grid>
    </MKBox>
  );
}

export default GeneralInfoForm;
